import * as React from "react";
import {
  Dispatch,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Fade, ListSubheader, styled } from "@mui/material";
import {
  MobileView,
  SingleViewPerLayoutBreakPoint,
} from "../../util/aKaunGeneralUtil";
import { DiagnosisOverview, QualifiedFeature } from "fe-common/graphql";
import {
  ScrollableContainer,
  scrollableStyleByColor,
} from "fe-common/ui/commonStyles";
import { AgentDiseaseItem } from "./agentDiseaseItem";
import { useCalculateFindingPredictives } from "../../util/FindingStrengthCalculator";
import { useHistory } from "react-router-dom";
import { useRouteMatch } from "react-router";
import {
  DDSection,
  DDSortType,
  DiseaseCluster,
  getFeaturesCountFromSections,
} from "fe-common/hooks/sortFeaturesWithCrs";
import { useDiagnosticNavigation } from "./useDiagnosticNavigation";
import { MobileOnly } from "./util/mobileOnly";
import Sticky from "react-stickynode";
import { Flipped, Flipper } from "react-flip-toolkit";
import LinearProgress from "@mui/material/LinearProgress";
import { ddListZIndex, NavBarZIndex } from "fe-common/ui/stacking";
import { usePatientCase } from "./usePatientCase";
import { SortSelect } from "./common/sortSelect";
import { useScreenSize } from "./util/useScreenSize";
import { removeLastRoute } from "../../util/pathUtils";
import { AgentRoute } from "./util/agentRoutes";
import { usePulse } from "../../util/usePule";
import { DiseaseItemMenu } from "./diseaseItemMenu";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { DDActionsMenu } from "./ddActionsMenu";
import { SearchToAddPlanDisease } from "./searchToAddPlanDisease";
import { useGetDiseasesInPlan } from "./hooks/useGetDiseasesInPlan";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { DataInputMobileSidePadding } from "./common/styles/consts";
import { ImproveDiagnosisButton } from "./nextStep/improveDiagnosisButton";
import { commonBoxShadowStyle } from "fe-common/ui/boxShadow";
import { AddDiseaseButton } from "./common/buttons/addDiseaseButton";
import { safeScroll } from "./scroll/safeScroll";
import { NoDiseasesIcon } from "./ddPanel/noDiseasesIcon";
import { DDStatus, DDStatusData } from "./updateDDToState";
import { DDErrorIcon } from "./ddPanel/ddErrorIcon";
import { HowItWorksTopLink } from "./santana/howItWorksTopLink";
import { getVersion } from "../../VersionInfo";

//region [[ Styles ]]
const DiagnosisViewWrapper = styled("div")({});

const DesktopImproveDiagnosisButton = styled(ImproveDiagnosisButton)(
  ({ theme }) => ({
    zIndex: ddListZIndex,
    "& .fab-icon": {
      height: "42px",
      padding: 0,
      paddingRight: "16px",
      ...commonBoxShadowStyle,
    },
    "& .add-icon-container": {
      height: "30px",
      width: "30px",
      margin: "7px",
    },
    [theme.breakpoints.down(MobileView)]: {
      display: "none",
    },
  }),
);

const TitleHeaderHeight = 50;

const ResultsList = styled(ScrollableContainer)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  [theme.breakpoints.down(MobileView)]: {
    position: "relative",
  },
  [theme.breakpoints.up(SingleViewPerLayoutBreakPoint)]: {
    maxHeight: `calc(100% - ${TitleHeaderHeight + 30}px)`, // 30px is the content wrapper padding
    ...scrollableStyleByColor(KahunColorMap.ActionBackground),
    padding: "0",
    marginRight: "-32px",
    paddingRight: "20px",
    position: "relative",
  },
}));

const TitleHeader = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  [theme.breakpoints.up(SingleViewPerLayoutBreakPoint)]: {
    height: `${TitleHeaderHeight}px`,
    background: KahunColorMap.MainBackground,
  },
  [theme.breakpoints.down(MobileView)]: {
    flexFlow: "column",
    backgroundColor: KahunColorMap.Secondary,
    color: "white",
    fontSize: "20px",
    fontWeight: 700,
    paddingTop: "16px",
    paddingBottom: "12px",
    paddingLeft: `${DataInputMobileSidePadding}px`,
    paddingRight: `${DataInputMobileSidePadding}px`,
    marginLeft: `-${DataInputMobileSidePadding}px`,
    marginRight: `-${DataInputMobileSidePadding}px`,
  },
}));

const HowItWorks = styled(HowItWorksTopLink)({
  paddingBottom: "14px",
});

const HeaderBottomRow = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const StickyStyled = styled(Sticky)(({ theme }) => ({
  width: "100%",
}));

const DdMainTitle = styled("span")(({ theme }) => ({
  fontSize: "23px",
  fontWeight: 700,
  color: KahunColorMap.Default,
  [theme.breakpoints.down(1465)]: {
    fontSize: "20px",
  },
  [theme.breakpoints.down(MobileView)]: {
    fontSize: "18px",
    color: "white",
    fontWeight: 700,
  },
}));

const ResultsNumber = styled("div")(({ theme }) => ({
  color: "#797879",
  fontSize: "13px",
  fontWeight: 500,
  [theme.breakpoints.up(MobileView)]: {
    fontSize: "16px",
  },
}));

const StyledListSubHeader = styled(ListSubheader)(({ theme }) => ({
  paddingLeft: "0px",
  textTransform: "uppercase",
  fontWeight: 300,
  fontSize: "17px",
  lineHeight: "19px",
  marginTop: "11px",
  marginBottom: 0,
  color: KahunColorMap.DefaultLight,
  [theme.breakpoints.down(MobileView)]: {
    color: KahunColorMap.SecondaryDark,
    marginTop: "20px",
    marginBottom: "7px",
    fontSize: "13px",
    lineHeight: "14px",
    fontWeight: 500,
  },
}));

const LoadingArea = styled("div")({
  display: "flex",
  flexDirection: "column",
});

const HeaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
}));

const DDActions = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  flexDirection: "row",
  alignItems: "center",
  marginRight: "-10px",
  [theme.breakpoints.down(MobileView)]: {
    marginRight: "-10px",
    alignItems: "center",
  },
}));

const NoDiseasesContainer = styled("div")({
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const SectionList = styled("div")({
  display: "flex",
  flexDirection: "column",
  marginBottom: "25px",
});

const DiseaseItemWrapper = styled("div")({});
//endregion [[ Styles ]]

//region [[ Props ]]
export interface DiagnosisViewProps {
  className?: string;
  selection: QualifiedFeature;
  setSelection: Dispatch<QualifiedFeature>;
  loading: boolean;
  data: DiagnosisOverview;
  ddStatus: DDStatusData;
  featuresSections: DDSection[];
  sortType: DDSortType;
  handleSortChange: (sortType: DDSortType) => void;
}

interface MenuItemSelect {
  anchor: HTMLElement;
  feature: QualifiedFeature;
}
//endregion [[ Props ]]

//region [[ Functions ]]

const SKELETON_ITEMS_COUNT = 11;
const tempElements = [];
for (let i = 0; i < SKELETON_ITEMS_COUNT; i++) {
  tempElements.push({
    qf: {
      id: "",
      name: Math.random()
        .toString(36)
        .replace(/[^a-z]+/g, "")
        .substr(0, 5),
      negate: false,
      feature: null,
      qualifiers: [],
      mandatory: null,
    },
    crs: [],
  });
}

//endregion [[ Functions ]]

export const DiagnosisView = memo(({ ...props }: DiagnosisViewProps) => {
  const {
    selection,
    setSelection,
    loading,
    data,
    featuresSections,
    ddStatus,
  } = props;

  useEffect(() => {
    if (ddStatus && ddStatus.status === DDStatus.ERROR && !loading) {
      evt({
        category: CATEGORY.DD_STATUS,
        action: ACTION.DD_ERROR,
        label: "v" + getVersion(),
      });
    } else if (
      ddStatus &&
      ddStatus.status === DDStatus.INSUFFICIENT_PATIENT_DATA
    ) {
      evt({
        category: CATEGORY.DD_STATUS,
        action: ACTION.DD_INSUFFICIENT_DATA,
      });
    }
  }, [ddStatus, loading]);

  const diseasesInPlan = useGetDiseasesInPlan();

  const {
    handleDiseasesToAdd,
    patientCase: { diseasesToDiscard, gender },
  } = usePatientCase();

  const listRef = useRef();

  const {
    setScrollLocation,
    ddScrollLocation: scrollLocation,
    setExpandedDisease: setExpandedItem,
    expandedDiseases: expandedItems,
  } = useDiagnosticNavigation();

  const { isMobile } = useScreenSize();

  const handleItemSelected = (disease) => {
    setSelection(disease);
  };

  const crs = data?.clinicalReasoning;

  const findingPredictiveCalculator = useCalculateFindingPredictives(
    crs?.length ? crs : null,
  );

  const match = useRouteMatch();

  // Clean "/dd"  from path in the end
  const matchPath = removeLastRoute(match.path);

  const history = useHistory();

  const [menuAnchor, setMenuAnchor] = useState<MenuItemSelect>(null);

  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<HTMLElement>(null);

  const handleShowDiseaseMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    feature,
  ) => {
    evt({
      category: CATEGORY.AGENT_DD_LIST,
      action: ACTION.OPEN_DISEASE_MENU,
      label: `feature: ${feature.name}`,
    });
    const { currentTarget } = e;
    setMenuAnchor({ anchor: currentTarget, feature });
    e.stopPropagation();
  };

  const handleCloseSettings = () => {
    setMenuAnchor(null);
  };

  const handleCloseActionsMenu = () => {
    setActionsMenuAnchor(null);
  };

  useLayoutEffect(() => {
    if (listRef?.current) {
      setImmediate(() => safeScroll(listRef, scrollLocation, "auto"));
    }
  }, [scrollLocation, listRef]);

  const handleGraphLinkClicked = (id: string) => {
    if (listRef?.current) {
      // @ts-ignore
      setScrollLocation(listRef.current.scrollTop);
    }
    history.push({
      pathname: `/agent/expanded/${id}`,
      search: history.location.search,
    });
  };

  const getExpandedItem = (id) => (loading ? false : expandedItems[id]);

  const resultsCount = getFeaturesCountFromSections(featuresSections);

  const handleSortChange = (event) => {
    props.handleSortChange(event);
  };

  const flipKey =
    featuresSections
      .flatMap(({ elements }) => elements?.map((f) => f?.qf?.id))
      .join(",") + loading;

  const diseaseItemClickHandler = useCallback(
    (qfId: string) => (expanded) => {
      if (isMobile) {
        history.push(`${matchPath}/${AgentRoute.ExpandedDisease}/${qfId}`);
      }
      setExpandedItem(qfId, expanded);
    },
    [isMobile, history, matchPath, setExpandedItem],
  );

  const toAgentDiseaseItem = (
    fWithCrs: DiseaseCluster,
    index,
    sectionTitle: string,
    sortType: DDSortType,
  ) => {
    const isInPlan = diseasesInPlan.some(
      (disease) => disease.disease.id === fWithCrs.qf.id,
    );

    const isMostLikely =
      sectionTitle === "Most Likely" || sortType === DDSortType.ByOrganSystem;

    const findingsStrength =
      findingPredictiveCalculator &&
      findingPredictiveCalculator(fWithCrs.crs[0]);

    return (
      <Flipped
        key={fWithCrs.qf.id}
        flipId={`${fWithCrs.qf.id}`}
        stagger={true}
        delayUntil={"sections"}
      >
        <DiseaseItemWrapper>
          <AgentDiseaseItem
            key={fWithCrs.qf?.id}
            sectionTitle={sectionTitle}
            inPlan={isInPlan}
            isMostLikely={isMostLikely}
            loading={props.loading}
            index={index}
            selected={fWithCrs.qf?.id === selection?.id}
            setSelection={() => handleItemSelected(fWithCrs.qf)}
            strength={fWithCrs.normalizedStrength}
            findingsStrength={findingsStrength}
            moreInfoBtnTitle={"More Info"}
            onGraphLinkClick={handleGraphLinkClicked}
            isExpanded={!isMobile && getExpandedItem(fWithCrs.qf?.id)}
            onExpand={diseaseItemClickHandler(fWithCrs.qf?.id)}
            onShowDiseaseMenu={handleShowDiseaseMenu}
            fWithCrs={fWithCrs}
          />
        </DiseaseItemWrapper>
      </Flipped>
    );
  };

  const SKELETON_ITEMS_COUNT = 5;
  const dummyElements = Array.apply(null, Array(SKELETON_ITEMS_COUNT)).map(
    (i) => ({
      qf: {
        id: `dummy-${i}`,
        name: Math.random()
          .toString(36)
          .replace(/[^a-z]+/g, "")
          .substr(0, 5),
        negate: false,
        feature: null,
        qualifiers: [],
        mandatory: null,
      },
      crs: [],
    }),
  );
  const toDummyDiseaseItem = (dummy, index) => (
    <Flipped key={`dummy-${index}`} flipId={`dummy-${index}`}>
      <DiseaseItemWrapper>
        <AgentDiseaseItem
          fWithCrs={dummy}
          dummy
          index={index}
          sectionTitle={""}
        />
      </DiseaseItemWrapper>
    </Flipped>
  );

  const discardedDiseaseFilter = useCallback(
    (e) => !diseasesToDiscard || !diseasesToDiscard.includes(e.qf),
    [diseasesToDiscard],
  );

  const handleDiseaseAddedToPlan = (disease: QualifiedFeature) => {
    if (disease) {
      handleDiseasesToAdd(disease);
    }
  };

  const getGlobalIndex = (fsIndex, index) => {
    let globalIndex = index;
    for (let i = 0; i < fsIndex; i++) {
      globalIndex += featuresSections[i].elements.length;
    }
    return globalIndex;
  };

  const { transparentPulse, withAnimation } = usePulse({ disabled: !loading });

  return (
    <DiagnosisViewWrapper className={props.className}>
      <TitleHeader>
        <MobileOnly>
          <HowItWorks className={"how-it-works"} />
        </MobileOnly>
        <HeaderBottomRow>
          <StickyStyled enabled innerZ={NavBarZIndex - 2}>
            <HeaderContainer>
              <DdMainTitle>Suggested Diseases</DdMainTitle>
              {!loading && (
                <DDActions>
                  <DesktopImproveDiagnosisButton />
                  <AddDiseaseButton />
                  <SortSelect
                    sortType={props.sortType}
                    handleSortChange={handleSortChange}
                  />
                </DDActions>
              )}
            </HeaderContainer>
          </StickyStyled>
          <MobileOnly>
            <ResultsNumber
              className={`${loading ? transparentPulse : ""} ${withAnimation}`}
            />
          </MobileOnly>
        </HeaderBottomRow>
      </TitleHeader>
      <ResultsList ref={listRef}>
        <Fade in={loading} unmountOnExit mountOnEnter>
          <LoadingArea>
            <LinearProgress color="primary" />
          </LoadingArea>
        </Fade>
        <Flipper flipKey={flipKey} staggerConfig={{ default: { speed: 0.9 } }}>
          {ddStatus &&
            ddStatus.status === DDStatus.INSUFFICIENT_PATIENT_DATA &&
            !loading && (
              <NoDiseasesContainer>
                <NoDiseasesIcon />
              </NoDiseasesContainer>
            )}
          {ddStatus && ddStatus.status === DDStatus.ERROR && !loading && (
            <NoDiseasesContainer>
              <DDErrorIcon ddStatus={ddStatus} />
            </NoDiseasesContainer>
          )}
          {featuresSections.length > 0 &&
            featuresSections.map(({ title, elements }, fsIndex) => (
              <Flipped key={`section-${fsIndex}`} flipId={`sections-${title}`}>
                {elements.length > 0 && (
                  <SectionList key={title}>
                    {!!title && (
                      <StyledListSubHeader disableSticky>
                        {title}
                      </StyledListSubHeader>
                    )}
                    {/* While there is no items for now, render dummies for loading*/}
                    {loading &&
                      !resultsCount &&
                      dummyElements.map(toDummyDiseaseItem)}
                    {elements
                      ?.filter(discardedDiseaseFilter)
                      .map((value, index) =>
                        toAgentDiseaseItem(
                          value,
                          getGlobalIndex(fsIndex, index),
                          title,
                          props.sortType,
                        ),
                      )}
                  </SectionList>
                )}
              </Flipped>
            ))}
        </Flipper>
      </ResultsList>
      <DiseaseItemMenu
        id="disease-options"
        anchorEl={menuAnchor?.anchor}
        open={!!menuAnchor}
        onClose={handleCloseSettings}
        selectedFeature={menuAnchor?.feature}
      />
      <DDActionsMenu
        id="disease-options"
        anchorEl={actionsMenuAnchor}
        open={!!actionsMenuAnchor}
        onClose={handleCloseActionsMenu}
      />
      <SearchToAddPlanDisease
        onDiseaseAdded={handleDiseaseAddedToPlan}
        gender={gender}
      />
    </DiagnosisViewWrapper>
  );
});
